import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Lock, LogOut, Users, ListChecks, Bell, Star, StarOff, StickyNote, Pencil, ChevronDown, AlertTriangle, Compass, Mail } from "lucide-react";
import { apiFetch, PawIcon, my5Label } from "@/lib/brand";

const TABS = [
    { id: "involved", label: "Get Involved", icon: Users },
    { id: "discovery", label: "Discovery", icon: Compass },
    { id: "my5", label: "MY 5 Drops", icon: ListChecks },
    { id: "waitlist", label: "Merch Waitlist", icon: Bell },
    { id: "newsletter", label: "Newsletter", icon: Mail },
];

const fmtDate = (iso) => {
    try {
        return new Date(iso).toLocaleString();
    } catch {
        return iso;
    }
};

const FOLLOW_UP_DAYS = 7;

const lastActivity = (e) => {
    const times = [e.created_at, ...(e.admin_notes || []).flatMap((n) => [n.created_at, n.updated_at])]
        .filter(Boolean)
        .map((t) => new Date(t).getTime())
        .filter((t) => !Number.isNaN(t));
    return times.length ? Math.max(...times) : 0;
};

const staleDays = (e) => Math.floor((Date.now() - lastActivity(e)) / 864e5);

const isStale = (e) => staleDays(e) >= FOLLOW_UP_DAYS;

function StaleBadge({ entry }) {
    if (!isStale(entry)) return null;
    return (
        <span
            className="font-mono text-[10px] uppercase tracking-[0.2em] border border-amber-400/50 text-amber-300 rounded-full px-3 py-1"
            data-testid={`stale-badge-${entry.id}`}
        >
            Follow-up needed · {staleDays(entry)}d idle
        </span>
    );
}

function NotesPanel({ kind, entry, token, onNotes }) {
    const [open, setOpen] = useState(false);
    const [draft, setDraft] = useState("");
    const [saving, setSaving] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [editText, setEditText] = useState("");
    const notes = entry.admin_notes || [];

    const addNote = async () => {
        if (!draft.trim()) return;
        setSaving(true);
        try {
            const d = await apiFetch(`/admin/${kind}/${entry.id}/notes`, { method: "POST", body: { text: draft.trim() }, token });
            onNotes([...notes, d.note]);
            setDraft("");
            toast.success("Note added — visible only to you.");
        } catch (e) {
            toast.error(e.message);
        } finally {
            setSaving(false);
        }
    };

    const saveEdit = async (noteId) => {
        if (!editText.trim()) return;
        try {
            const d = await apiFetch(`/admin/${kind}/${entry.id}/notes/${noteId}`, { method: "PATCH", body: { text: editText.trim() }, token });
            onNotes(notes.map((n) => (n.id === noteId ? d.note : n)));
            setEditingId(null);
            toast.success("Note updated.");
        } catch (e) {
            toast.error(e.message);
        }
    };

    return (
        <div className="mt-4 border-t border-white/10 pt-4">
            <button
                onClick={() => setOpen(!open)}
                data-testid={`admin-notes-toggle-${entry.id}`}
                className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400 hover:text-purp-neon transition-colors"
            >
                <StickyNote className="w-3.5 h-3.5" /> Private Notes ({notes.length})
                <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
            </button>
            {open && (
                <div className="mt-4 space-y-3" data-testid={`admin-notes-panel-${entry.id}`}>
                    {notes.map((n) => (
                        <div key={n.id} className="rounded-xl bg-obsidian/70 border border-white/10 p-4" data-testid={`admin-note-item-${n.id}`}>
                            {editingId === n.id ? (
                                <div className="space-y-2">
                                    <textarea
                                        rows={2}
                                        className="w-full rounded-lg bg-obsidian border border-purp/40 px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-purp"
                                        value={editText}
                                        onChange={(e) => setEditText(e.target.value)}
                                        maxLength={1000}
                                        data-testid={`admin-note-edit-input-${n.id}`}
                                    />
                                    <div className="flex gap-2">
                                        <button onClick={() => saveEdit(n.id)} className="rounded-full bg-purp px-4 py-1.5 font-mono text-[10px] uppercase tracking-[0.15em] font-bold text-obsidian" data-testid={`admin-note-save-${n.id}`}>Save</button>
                                        <button onClick={() => setEditingId(null)} className="rounded-full border border-white/15 px-4 py-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-slate-400 hover:text-slate-200" data-testid={`admin-note-cancel-${n.id}`}>Cancel</button>
                                    </div>
                                </div>
                            ) : (
                                <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                        <p className="text-sm text-slate-300 whitespace-pre-wrap leading-relaxed">{n.text}</p>
                                        <p className="mt-2 font-mono text-[10px] text-slate-600">
                                            {fmtDate(n.created_at)}{n.updated_at !== n.created_at ? " · edited" : ""}
                                        </p>
                                    </div>
                                    <button
                                        onClick={() => { setEditingId(n.id); setEditText(n.text); }}
                                        className="text-slate-500 hover:text-purp-neon transition-colors shrink-0 p-1"
                                        data-testid={`admin-note-edit-${n.id}`}
                                        aria-label="Edit note"
                                    >
                                        <Pencil className="w-3.5 h-3.5" />
                                    </button>
                                </div>
                            )}
                        </div>
                    ))}
                    <div className="flex gap-2 items-start">
                        <textarea
                            rows={2}
                            placeholder="Follow-ups, conversations, status, next steps — only you see this."
                            className="flex-1 rounded-xl bg-obsidian/80 border border-white/15 px-4 py-3 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-purp focus:ring-1 focus:ring-purp/60 transition-colors"
                            value={draft}
                            onChange={(e) => setDraft(e.target.value)}
                            maxLength={1000}
                            data-testid={`admin-note-input-${entry.id}`}
                        />
                        <button
                            onClick={addNote}
                            disabled={saving}
                            className="rounded-full bg-purp px-5 py-3 font-mono text-[10px] uppercase tracking-[0.15em] font-bold text-obsidian hover:shadow-[0_0_24px_rgba(168,85,247,0.5)] transition-shadow disabled:opacity-60"
                            data-testid={`admin-note-add-${entry.id}`}
                        >
                            {saving ? "…" : "Add"}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

function ForgotPasswordInline() {
    const [open, setOpen] = useState(false);
    const [fEmail, setFEmail] = useState("");
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);
    const [err, setErr] = useState("");

    const submit = async () => {
        if (!fEmail.trim()) {
            setErr("Enter your admin email.");
            return;
        }
        setSending(true);
        setErr("");
        try {
            await apiFetch("/auth/forgot-password", { method: "POST", body: { email: fEmail.trim() } });
            setSent(true);
        } catch (e2) {
            setErr(e2.message);
        } finally {
            setSending(false);
        }
    };

    if (sent) {
        return (
            <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.15em] text-volt leading-relaxed" data-testid="forgot-success">
                If that email is registered, a reset link is on its way. Check your inbox.
            </p>
        );
    }
    if (!open) {
        return (
            <button
                type="button"
                onClick={() => setOpen(true)}
                data-testid="forgot-open"
                className="mt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 hover:text-purp-neon transition-colors"
            >
                Forgot password?
            </button>
        );
    }
    return (
        <div className="mt-4">
            <div className="flex gap-2" data-testid="forgot-form">
                <input
                    type="email"
                    value={fEmail}
                    onChange={(e) => setFEmail(e.target.value)}
                    placeholder="Admin email"
                    aria-label="Admin email"
                    data-testid="forgot-email-input"
                    className="flex-1 min-w-0 rounded-xl bg-obsidian/80 border border-white/15 px-4 py-3 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-purp focus:ring-1 focus:ring-purp/60 transition-colors"
                />
                <button
                    type="button"
                    onClick={submit}
                    disabled={sending}
                    data-testid="forgot-submit"
                    className="rounded-full bg-purp px-5 py-3 font-mono text-[10px] uppercase tracking-[0.15em] font-bold text-obsidian hover:shadow-[0_0_24px_rgba(168,85,247,0.5)] transition-shadow disabled:opacity-60 shrink-0"
                >
                    {sending ? "…" : "Send Link"}
                </button>
            </div>
            {err && <p className="mt-2 text-xs text-red-400" data-testid="forgot-error">{err}</p>}
        </div>
    );
}

export function AdminReset() {
    const [params] = useSearchParams();
    const resetToken = params.get("token") || "";
    const [pw, setPw] = useState("");
    const [pw2, setPw2] = useState("");
    const [saving, setSaving] = useState(false);
    const [done, setDone] = useState(false);
    const [error, setError] = useState("");

    const submit = async (e) => {
        e.preventDefault();
        if (pw.length < 8) {
            setError("Password must be at least 8 characters.");
            return;
        }
        if (pw !== pw2) {
            setError("Passwords don't match.");
            return;
        }
        setSaving(true);
        setError("");
        try {
            await apiFetch("/auth/reset-password", { method: "POST", body: { token: resetToken, new_password: pw } });
            setDone(true);
        } catch (err) {
            setError(err.message);
        } finally {
            setSaving(false);
        }
    };

    const inputCls =
        "w-full rounded-xl bg-obsidian/80 border border-white/15 px-4 py-3.5 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-purp focus:ring-1 focus:ring-purp/60 transition-colors";

    return (
        <div className="min-h-screen flex items-center justify-center px-5 pt-24 pb-16 relative" data-testid="admin-reset-page">
            <div className="absolute inset-0 bg-radial-purp" />
            <div className="relative w-full max-w-md rounded-3xl bg-panel/80 backdrop-blur-md border border-purp/35 card-glow p-8 md:p-10">
                <span className="w-12 h-12 rounded-2xl bg-obsidian border border-purp/40 flex items-center justify-center">
                    <PawIcon className="w-6 h-6 text-purp" />
                </span>
                <h1 className="mt-5 font-display font-extrabold uppercase text-4xl leading-none">
                    New <span className="italic text-purp">Password</span>
                </h1>
                {!resetToken ? (
                    <p className="mt-4 text-sm text-red-400" data-testid="reset-no-token">
                        This reset link is missing its token. Request a fresh one from the login page.
                    </p>
                ) : done ? (
                    <div data-testid="reset-success">
                        <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.15em] text-volt leading-relaxed">
                            Password updated. You're clear to enter HQ.
                        </p>
                        <Link
                            to="/admin"
                            data-testid="reset-login-link"
                            className="mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-purp px-7 py-3.5 font-mono text-xs uppercase tracking-[0.2em] font-bold text-obsidian transition-shadow duration-300 hover:shadow-[0_0_40px_rgba(168,85,247,0.65)]"
                        >
                            Back to Login
                        </Link>
                    </div>
                ) : (
                    <form onSubmit={submit} className="mt-8 space-y-4" data-testid="reset-form">
                        <input type="password" className={inputCls} placeholder="New password (8+ characters)" value={pw} onChange={(e) => setPw(e.target.value)} required data-testid="reset-password-input" />
                        <input type="password" className={inputCls} placeholder="Confirm new password" value={pw2} onChange={(e) => setPw2(e.target.value)} required data-testid="reset-confirm-input" />
                        {error && <p className="text-sm text-red-400" data-testid="reset-error">{error}</p>}
                        <button
                            type="submit"
                            disabled={saving}
                            data-testid="reset-submit"
                            className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-purp px-7 py-3.5 font-mono text-xs uppercase tracking-[0.2em] font-bold text-obsidian transition-shadow duration-300 hover:shadow-[0_0_40px_rgba(168,85,247,0.65)] disabled:opacity-60"
                        >
                            <Lock className="w-4 h-4" /> {saving ? "Updating..." : "Set New Password"}
                        </button>
                    </form>
                )}
            </div>
        </div>
    );
}

export default function Admin() {
    const [token, setToken] = useState(() => localStorage.getItem("purp_admin_token") || "");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [tab, setTab] = useState("involved");
    const [data, setData] = useState({ involved: [], my5: [], waitlist: [], discovery: [], newsletter: [] });

    const logout = () => {
        localStorage.removeItem("purp_admin_token");
        setToken("");
        setData({ involved: [], my5: [], waitlist: [], discovery: [], newsletter: [] });
    };

    const load = async (tok) => {
        const [involved, my5, waitlist, discovery, newsletter] = await Promise.all([
            apiFetch("/admin/get-involved", { token: tok }),
            apiFetch("/admin/my5", { token: tok }),
            apiFetch("/admin/notify", { token: tok }),
            apiFetch("/admin/discovery", { token: tok }),
            apiFetch("/admin/newsletter", { token: tok }),
        ]);
        setData({ involved, my5, waitlist, discovery, newsletter });
    };

    useEffect(() => {
        if (!token) return;
        load(token).catch((e) => {
            if (/token|authenticated|access/i.test(e.message)) {
                logout();
            } else {
                toast.error(e.message);
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    const login = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError("");
        try {
            const d = await apiFetch("/auth/login", { method: "POST", body: { email, password } });
            localStorage.setItem("purp_admin_token", d.access_token);
            setToken(d.access_token);
            toast.success("Welcome to Network HQ.");
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const toggleFeature = async (id, featured) => {
        try {
            await apiFetch(`/admin/my5/${id}/feature`, { method: "PATCH", body: { featured }, token });
            setData((d) => ({ ...d, my5: d.my5.map((m) => (m.id === id ? { ...m, featured } : m)) }));
            toast.success(featured ? "Featured on the MY 5 page." : "Unfeatured.");
        } catch (e) {
            toast.error(e.message);
        }
    };

    const updateNotes = (listKey, id, notes) =>
        setData((d) => ({ ...d, [listKey]: d[listKey].map((e) => (e.id === id ? { ...e, admin_notes: notes } : e)) }));

    const inputCls =
        "w-full rounded-xl bg-obsidian/80 border border-white/15 px-4 py-3.5 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-purp focus:ring-1 focus:ring-purp/60 transition-colors";

    if (!token) {
        return (
            <div className="min-h-screen flex items-center justify-center px-5 pt-24 pb-16 relative" data-testid="admin-login-page">
                <div className="absolute inset-0 bg-radial-purp" />
                <div className="relative w-full max-w-md rounded-3xl bg-panel/80 backdrop-blur-md border border-purp/35 card-glow p-8 md:p-10">
                    <span className="w-12 h-12 rounded-2xl bg-obsidian border border-purp/40 flex items-center justify-center">
                        <PawIcon className="w-6 h-6 text-purp" />
                    </span>
                    <h1 className="mt-5 font-display font-extrabold uppercase text-4xl leading-none">
                        Network <span className="italic text-purp">HQ</span>
                    </h1>
                    <p className="mt-2 font-mono text-[11px] uppercase tracking-[0.25em] text-slate-500">Admin access only</p>
                    <form onSubmit={login} className="mt-8 space-y-4" data-testid="admin-login-form">
                        <input type="email" className={inputCls} placeholder="Admin email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="admin-email-input" />
                        <input type="password" className={inputCls} placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="admin-password-input" />
                        {error && <p className="text-sm text-red-400" data-testid="admin-login-error">{error}</p>}
                        <button
                            type="submit"
                            disabled={loading}
                            data-testid="admin-login-submit"
                            className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-purp px-7 py-3.5 font-mono text-xs uppercase tracking-[0.2em] font-bold text-obsidian transition-shadow duration-300 hover:shadow-[0_0_40px_rgba(168,85,247,0.65)] disabled:opacity-60"
                        >
                            <Lock className="w-4 h-4" /> {loading ? "Checking..." : "Enter HQ"}
                        </button>
                        <ForgotPasswordInline />
                    </form>
                </div>
            </div>
        );
    }

    const counts = { involved: data.involved.length, my5: data.my5.length, waitlist: data.waitlist.length, discovery: data.discovery.length, newsletter: data.newsletter.length };
    const staleCount = [...data.involved, ...data.my5, ...data.discovery].filter(isStale).length;

    return (
        <div className="min-h-screen pt-28 md:pt-36 pb-20" data-testid="admin-dashboard">
            <div className="max-w-6xl mx-auto px-5 md:px-8">
                <div className="flex flex-wrap items-center justify-between gap-4">
                    <div>
                        <p className="font-mono text-[11px] uppercase tracking-[0.35em] text-purp-neon flex items-center gap-3">
                            <span className="inline-block w-2 h-2 rounded-full bg-volt animate-pulse-dot" /> Network HQ
                        </p>
                        <h1 className="mt-3 font-display font-extrabold uppercase text-4xl md:text-5xl leading-none">
                            Submission <span className="italic text-purp">Inbox</span>
                        </h1>
                    </div>
                    <button
                        onClick={logout}
                        data-testid="admin-logout-btn"
                        className="inline-flex items-center gap-2 rounded-full border border-white/15 px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400 hover:text-purp-neon hover:border-purp/50 transition-colors"
                    >
                        <LogOut className="w-4 h-4" /> Log Out
                    </button>
                </div>

                <div className="mt-10 flex flex-wrap gap-2" data-testid="admin-tabs">
                    {TABS.map((t) => (
                        <button
                            key={t.id}
                            onClick={() => setTab(t.id)}
                            data-testid={`admin-tab-${t.id}`}
                            className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.2em] border transition-colors duration-300 ${
                                tab === t.id
                                    ? "bg-purp text-obsidian border-purp font-bold"
                                    : "border-white/15 text-slate-400 hover:border-purp/50 hover:text-purp-neon"
                            }`}
                        >
                            <t.icon className="w-4 h-4" /> {t.label}
                            <span className={`rounded-full px-2 py-0.5 text-[10px] ${tab === t.id ? "bg-obsidian/20" : "bg-white/10"}`}>{counts[t.id]}</span>
                        </button>
                    ))}
                </div>

                {staleCount > 0 && (
                    <div className="mt-6 rounded-2xl border border-amber-400/40 bg-amber-400/5 px-6 py-4 flex items-center gap-3" data-testid="followup-banner">
                        <AlertTriangle className="w-5 h-5 text-amber-300 shrink-0" />
                        <p className="font-mono text-xs uppercase tracking-[0.2em] text-amber-200">
                            {staleCount} {staleCount === 1 ? "entry needs" : "entries need"} follow-up — no note activity in {FOLLOW_UP_DAYS}+ days
                        </p>
                    </div>
                )}

                <div className="mt-8 space-y-4" data-testid="admin-list">                    {tab === "involved" &&
                        (data.involved.length ? (
                            data.involved.map((s) => (
                                <div key={s.id} className="rounded-2xl bg-panel/75 border border-white/10 p-6" data-testid={`admin-involved-${s.id}`}>
                                    <div className="flex flex-wrap items-center justify-between gap-3">
                                        <div>
                                            <p className="font-display font-bold uppercase text-xl">{s.name}</p>
                                            <p className="font-mono text-xs text-purp-neon">{s.email}</p>
                                        </div>
                                        <div className="text-right">
                                            <div className="flex items-center justify-end gap-2 flex-wrap">
                                                <StaleBadge entry={s} />
                                                <span className="font-mono text-[10px] uppercase tracking-[0.25em] border border-purp/50 text-purp-neon rounded-full px-3 py-1">{s.pathway}</span>
                                            </div>
                                            <p className="mt-2 font-mono text-[10px] text-slate-500">{fmtDate(s.created_at)}</p>
                                        </div>
                                    </div>
                                    {s.message && <p className="mt-4 text-sm text-slate-400 leading-relaxed">{s.message}</p>}
                                    <NotesPanel kind="get-involved" entry={s} token={token} onNotes={(notes) => updateNotes("involved", s.id, notes)} />
                                </div>
                            ))
                        ) : (
                            <p className="text-slate-500 font-mono text-sm" data-testid="admin-empty-involved">No Get Involved entries yet.</p>
                        ))}

                    {tab === "my5" &&
                        (data.my5.length ? (
                            data.my5.map((s) => (
                                <div key={s.id} className={`rounded-2xl bg-panel/75 border p-6 ${s.featured ? "border-purp/50 card-glow" : "border-white/10"}`} data-testid={`admin-my5-${s.id}`}>
                                    <div className="flex flex-wrap items-center justify-between gap-3">
                                        <div>
                                            <p className="font-display font-bold uppercase text-xl">{s.name}</p>
                                            <p className="font-mono text-xs text-purp-neon">{my5Label(s.category)}{s.email ? ` — ${s.email}` : ""}</p>
                                            {(s.photo_url || s.handle || s.social_link) && (
                                                <div className="mt-2 flex items-center gap-2 flex-wrap">
                                                    {s.photo_url && <img src={s.photo_url} alt="" className="w-7 h-7 rounded-full object-cover border border-purp/40" />}
                                                    <p className="font-mono text-[10px] text-slate-500 break-all">
                                                        {[s.handle && `@${s.handle}`, s.social_link, s.photo_url && "photo attached"].filter(Boolean).join(" · ")}
                                                    </p>
                                                </div>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-3 flex-wrap">
                                            <StaleBadge entry={s} />
                                            {s.featured && <span className="font-mono text-[10px] uppercase tracking-[0.25em] border border-volt/50 text-volt rounded-full px-3 py-1">Featured</span>}
                                            <button
                                                onClick={() => toggleFeature(s.id, !s.featured)}
                                                data-testid={`admin-feature-toggle-${s.id}`}
                                                className={`inline-flex items-center gap-2 rounded-full px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] font-bold transition-colors duration-300 ${
                                                    s.featured
                                                        ? "border border-white/20 text-slate-300 hover:border-red-400/60 hover:text-red-300"
                                                        : "bg-purp text-obsidian hover:shadow-[0_0_28px_rgba(168,85,247,0.55)]"
                                                }`}
                                            >
                                                {s.featured ? <><StarOff className="w-3.5 h-3.5" /> Unfeature</> : <><Star className="w-3.5 h-3.5" /> Feature</>}
                                            </button>
                                        </div>
                                    </div>
                                    <ol className="mt-4 space-y-1.5">
                                        {s.items.map((item, i) => (
                                            <li key={i} className="flex gap-3 items-baseline text-sm">
                                                <span className="font-display font-bold text-purp-neon shrink-0">{i + 1}</span>
                                                <span className="text-slate-400">{item}</span>
                                            </li>
                                        ))}
                                    </ol>
                                    <p className="mt-3 font-mono text-[10px] text-slate-500">{fmtDate(s.created_at)}</p>
                                    <NotesPanel kind="my5" entry={s} token={token} onNotes={(notes) => updateNotes("my5", s.id, notes)} />
                                </div>
                            ))
                        ) : (
                            <p className="text-slate-500 font-mono text-sm" data-testid="admin-empty-my5">No MY 5 submissions yet.</p>
                        ))}

                    {tab === "discovery" &&
                        (data.discovery.length ? (
                            data.discovery.map((s) => (
                                <div key={s.id} className="rounded-2xl bg-panel/75 border border-white/10 p-6" data-testid={`admin-discovery-${s.id}`}>
                                    <div className="flex flex-wrap items-center justify-between gap-3">
                                        <div>
                                            <p className="font-display font-bold uppercase text-xl">{s.name}</p>
                                            <p className="font-mono text-xs text-purp-neon">{s.email}{s.social ? ` — ${s.social}` : ""}</p>
                                        </div>
                                        <div className="flex items-center gap-2 flex-wrap justify-end">
                                            <StaleBadge entry={s} />
                                            <span className="font-mono text-[10px] uppercase tracking-[0.25em] border border-purp/50 text-purp-neon rounded-full px-3 py-1">{s.path}</span>
                                            {s.marketing_consent && <span className="font-mono text-[10px] uppercase tracking-[0.25em] border border-volt/50 text-volt rounded-full px-3 py-1">Email OK</span>}
                                        </div>
                                    </div>
                                    <div className="mt-4 flex flex-wrap gap-1.5">
                                        {(s.directions || []).map((d) => (
                                            <span key={d} className="font-mono text-[10px] uppercase tracking-[0.15em] bg-purp/10 border border-purp/30 text-purp-neon rounded-full px-2.5 py-1">{d}</span>
                                        ))}
                                    </div>
                                    <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Experience: {s.experience}</p>
                                    {(s.needs || []).length > 0 && (
                                        <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Needs: {s.needs.join(" · ")}</p>
                                    )}
                                    {s.goals && <p className="mt-3 text-sm text-slate-400 leading-relaxed"><span className="text-slate-200 font-semibold">Goals:</span> {s.goals}</p>}
                                    {s.ai_perspective && <p className="mt-2 text-sm text-slate-400 leading-relaxed"><span className="text-slate-200 font-semibold">On AI:</span> {s.ai_perspective}</p>}
                                    <p className="mt-3 font-mono text-[10px] text-slate-500">{fmtDate(s.created_at)}</p>
                                    <NotesPanel kind="discovery" entry={s} token={token} onNotes={(notes) => updateNotes("discovery", s.id, notes)} />
                                </div>
                            ))
                        ) : (
                            <p className="text-slate-500 font-mono text-sm" data-testid="admin-empty-discovery">No discovery submissions yet.</p>
                        ))}

                    {tab === "newsletter" &&
                        (data.newsletter.length ? (
                            data.newsletter.map((s) => (
                                <div key={s.id} className="rounded-2xl bg-panel/75 border border-white/10 px-6 py-4" data-testid={`admin-newsletter-${s.id}`}>
                                    <div className="flex items-center justify-between gap-3 flex-wrap">
                                        <p className="font-mono text-sm text-slate-200">{s.email}</p>
                                        <div className="flex items-center gap-3">
                                            {s.consent && <span className="font-mono text-[10px] uppercase tracking-[0.2em] border border-volt/50 text-volt rounded-full px-3 py-1">Consented</span>}
                                            <p className="font-mono text-[10px] text-slate-500">{fmtDate(s.created_at)}</p>
                                        </div>
                                    </div>
                                    <NotesPanel kind="newsletter" entry={s} token={token} onNotes={(notes) => updateNotes("newsletter", s.id, notes)} />
                                </div>
                            ))
                        ) : (
                            <p className="text-slate-500 font-mono text-sm" data-testid="admin-empty-newsletter">No newsletter signups yet.</p>
                        ))}

                    {tab === "waitlist" &&
                        (data.waitlist.length ? (
                            data.waitlist.map((s) => (
                                <div key={s.id} className="rounded-2xl bg-panel/75 border border-white/10 px-6 py-4" data-testid={`admin-waitlist-${s.id}`}>
                                    <div className="flex items-center justify-between gap-3 flex-wrap">
                                        <div>
                                            <p className="font-mono text-sm text-slate-200">{s.name || s.email}</p>
                                            {s.name && <p className="font-mono text-xs text-slate-500">{s.email}</p>}
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <span className={`rounded-full px-3 py-1 font-mono text-[9px] uppercase tracking-[0.15em] border ${s.interest?.startsWith("park-tester") ? "border-volt/50 text-volt" : "border-purp/40 text-purp-neon"}`}>
                                                {s.interest === "park-tester" ? "P.A.R.K. Tester" : s.interest === "park-tester-waitlist" ? "P.A.R.K. Waitlist" : s.interest === "pilot-waitlist" ? "Pilot Waitlist" : "Merch"}
                                            </span>
                                            <StaleBadge entry={s} />
                                            <p className="font-mono text-[10px] text-slate-500">{fmtDate(s.created_at)}</p>
                                        </div>
                                    </div>
                                    <NotesPanel kind="waitlist" entry={s} token={token} onNotes={(notes) => updateNotes("waitlist", s.id, notes)} />
                                </div>
                            ))
                        ) : (
                            <p className="text-slate-500 font-mono text-sm" data-testid="admin-empty-waitlist">No waitlist signups yet.</p>
                        ))}
                </div>
            </div>
        </div>
    );
}
